import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  Scan,
  Zap,
  Gem,
  Users,
  ArrowRight,
  Code,
  Wifi,
  Database,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Small reusable pieces
// ─────────────────────────────────────────────────────────────────────────────

function SectionLabel({ children, color = "#b44fff" }) {
  return (
    <p
      className="text-xs font-bold tracking-widest uppercase mb-3"
      style={{ color }}
    >
      {children}
    </p>
  );
}

function SectionHeading({ children, color, glow }) {
  return (
    <h2
      className="leading-tight mb-0"
      style={{
        fontFamily: "var(--font-display)", fontWeight: 700,
        fontSize:   "clamp(1.8rem, 4vw, 2.8rem)",
        color,
        textShadow: `0 0 26px ${glow || color}60`,
      }}
    >
      {children}
    </h2>
  );
}

function IconOrb({ gradient, glow, children }) {
  return (
    <div
      className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
      style={{
        background: gradient,
        boxShadow:  `0 8px 28px ${glow}55`,
      }}
    >
      {children}
    </div>
  );
}

function GlassCard({ children, className = "", accentColor }) {
  return (
    <div
      className={`rounded-2xl p-6 md:p-8 ${className}`}
      style={{
        background:     accentColor
          ? `linear-gradient(135deg, ${accentColor}0e, rgba(255,255,255,0.02))`
          : "rgba(255,255,255,0.03)",
        border:         `1px solid ${accentColor ? accentColor + "28" : "rgba(255,255,255,0.08)"}`,
        backdropFilter: "blur(20px)",
      }}
    >
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────────────────
function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: "#0a0a0f" }}>

      {/* Ambient background glow — static, no orbs needed on a content page */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 20% 10%, rgba(180,79,255,0.12) 0%, transparent 50%), " +
            "radial-gradient(ellipse at 80% 80%, rgba(0,245,255,0.08) 0%, transparent 50%)",
          zIndex: 0,
        }}
      />

      {/* ── CONTENT ──────────────────────────────────────────────────────── */}
      <div className="relative max-w-3xl mx-auto px-5 py-12 md:py-16" style={{ zIndex: 10 }}>

        {/* Back link */}
        <button
          onClick={() => navigate("/")}
          className="group flex items-center gap-2 text-white/45 hover:text-white/80 transition-colors mb-12 cursor-pointer"
        >
          <ArrowRight
            className="rotate-180 group-hover:-translate-x-1 transition-transform"
            style={{ width: "1rem", height: "1rem" }}
          />
          <span className="text-sm">Back to Home</span>
        </button>

        {/* ── PAGE TITLE ─────────────────────────────────────────────────── */}
        <header className="mb-16">
          <p
            className="text-sm font-bold tracking-widest uppercase mb-4"
            style={{ color: "#b44fff" }}
          >
            Kandi Konnect
          </p>
          <h1
            className="leading-none mb-5"
            style={{
              fontFamily: "var(--font-display)", fontWeight: 700,
              fontSize:   "clamp(3rem, 10vw, 5.5rem)",
              color:      "#ff3cac",
              textShadow: "0 0 44px rgba(180,79,255,0.55)",
            }}
          >
            About Us
          </h1>
          <p className="text-lg text-white/60 max-w-xl leading-relaxed">
            Connecting the rave community, one bead at a time.
          </p>

          {/* Thin accent rule */}
          <div
            className="mt-8 h-px w-24"
            style={{ background: "linear-gradient(90deg, #b44fff, transparent)" }}
          />
        </header>

        {/* ── WHAT IS KANDI KONNECT? ──────────────────────────────────────── */}
        <section className="mb-16">
          <div className="flex items-start gap-5 mb-6">
            <IconOrb gradient="linear-gradient(135deg,#ff3cac,#b44fff)" glow="#b44fff">
              <Gem style={{ width: "1.5rem", height: "1.5rem", color: "#fff" }} />
            </IconOrb>
            <div className="pt-1">
              <SectionLabel color="#ff3cac">What We Do</SectionLabel>
              <SectionHeading color="#ff3cac" glow="#b44fff">
                What is Kandi Konnect?
              </SectionHeading>
            </div>
          </div>

          <GlassCard accentColor="#b44fff">
            <p className="text-white/75 leading-relaxed mb-4 text-base">
              Kandi Konnect is a revolutionary platform that brings the beautiful tradition
              of kandi trading into the digital age. We help ravers track their kandi
              collection, preserve the memories of connections made, and watch as their
              pieces travel across the globe spreading PLUR.
            </p>
            <p className="text-white/75 leading-relaxed text-base">
              Every kandi bracelet has a story — where it was made, who created it, and
              the incredible journey of connections it makes as it passes from friend to
              friend. We make those stories visible, trackable, and shareable.
            </p>
          </GlassCard>
        </section>

        {/* Divider */}
        <div className="mb-16 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

        {/* ── HOW IT WORKS ───────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="flex items-start gap-5 mb-8">
            <IconOrb gradient="linear-gradient(135deg,#b44fff,#00f5ff)" glow="#b44fff">
              <Zap style={{ width: "1.5rem", height: "1.5rem", color: "#fff" }} />
            </IconOrb>
            <div className="pt-1">
              <SectionLabel color="#b44fff">The Process</SectionLabel>
              <SectionHeading color="#b44fff" glow="#00f5ff">
                How It Works
              </SectionHeading>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                n: "1", color: "#ff3cac",
                title: "Scan Your Bead",
                body:  "Each kandi comes with a scannable NFC tag or QR code. Scan it to register in your digital collection.",
              },
              {
                n: "2", color: "#b44fff",
                title: "PLUR & Transfer",
                body:  "When you trade kandi, transfer ownership through the app. The new owner gets it added to their collection.",
              },
              {
                n: "3", color: "#00f5ff",
                title: "Track the Journey",
                body:  "Watch your kandi travel the world! See the complete history of who owned each piece.",
              },
            ].map(({ n, color, title, body }) => (
              <div
                key={n}
                className="rounded-2xl p-5 transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{
                  background:     `linear-gradient(135deg, ${color}14, rgba(255,255,255,0.02))`,
                  border:         `1px solid ${color}28`,
                  backdropFilter: "blur(20px)",
                }}
              >
                {/* Step badge */}
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-black text-white mb-4"
                  style={{ background: `linear-gradient(135deg, ${color}, ${color}aa)`, boxShadow: `0 4px 16px ${color}55` }}
                >
                  {n}
                </div>
                <h3 className="text-white font-bold mb-2">{title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="mb-16 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

        {/* ── TECHNOLOGY ─────────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="flex items-start gap-5 mb-8">
            <IconOrb gradient="linear-gradient(135deg,#00f5ff,#b44fff)" glow="#00f5ff">
              <Code style={{ width: "1.5rem", height: "1.5rem", color: "#fff" }} />
            </IconOrb>
            <div className="pt-1">
              <SectionLabel color="#00f5ff">Under the Hood</SectionLabel>
              <SectionHeading color="#00f5ff" glow="#b44fff">
                The Technology
              </SectionHeading>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              {
                icon:  <Wifi style={{ width: "1.25rem", height: "1.25rem", color: "#fff" }} />,
                grad:  "linear-gradient(135deg,#b44fff,#ff3cac)",
                glow:  "#b44fff",
                title: "NFC & QR Scanning",
                body:  "Each kandi can be equipped with a tiny NFC chip or QR code, making it easy to scan and track with any smartphone.",
              },
              {
                icon:  <Database style={{ width: "1.25rem", height: "1.25rem", color: "#fff" }} />,
                grad:  "linear-gradient(135deg,#00f5ff,#b44fff)",
                glow:  "#00f5ff",
                title: "Secure Cloud Storage",
                body:  "All kandi data is stored securely in the cloud, creating a permanent record of each piece's journey.",
              },
            ].map(({ icon, grad, glow, title, body }) => (
              <GlassCard key={title} className="flex gap-4 items-start transition-all duration-300 hover:-translate-y-1 cursor-default">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ background: grad, boxShadow: `0 4px 16px ${glow}44` }}
                >
                  {icon}
                </div>
                <div>
                  <h3 className="text-white font-bold mb-1">{title}</h3>
                  <p className="text-white/55 text-sm leading-relaxed">{body}</p>
                </div>
              </GlassCard>
            ))}
          </div>
        </section>

        {/* Divider */}
        <div className="mb-16 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

        {/* ── OUR INSPIRATION ────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="flex items-start gap-5 mb-8">
            <IconOrb gradient="linear-gradient(135deg,#ff3cac,#00f5ff)" glow="#ff3cac">
              <Heart style={{ width: "1.5rem", height: "1.5rem", color: "#fff" }} />
            </IconOrb>
            <div className="pt-1">
              <SectionLabel color="#ff3cac">The Why</SectionLabel>
              <SectionHeading color="#ff3cac" glow="#00f5ff">
                Our Inspiration
              </SectionHeading>
            </div>
          </div>

          {/* Pull quote */}
          <div
            className="rounded-2xl p-6 md:p-8 mb-4 relative overflow-hidden"
            style={{
              background:     "linear-gradient(135deg, rgba(255,60,172,0.1), rgba(180,79,255,0.08), rgba(0,245,255,0.06))",
              border:         "1px solid rgba(255,60,172,0.2)",
              backdropFilter: "blur(20px)",
            }}
          >
            {/* Giant quote mark */}
            <div
              className="absolute top-3 left-5 text-7xl font-black leading-none select-none pointer-events-none"
              style={{ color: "rgba(180,79,255,0.15)", fontFamily: "Georgia, serif" }}
            >
              "
            </div>

            <p className="text-white/80 leading-relaxed mb-4 relative pl-2">
              Kandi Konnect was born from a simple question:{" "}
              <span className="text-white font-semibold italic">
                "I wonder where this bracelet is now?"
              </span>{" "}
              After giving away a special piece of kandi at a festival, our founder
              wished there was a way to know if it was still spreading love.
            </p>
            <p className="text-white/75 leading-relaxed mb-4 pl-2">
              The rave community has always been about connection, acceptance, and PLUR —
              Peace, Love, Unity, and Respect. Kandi trading is one of the most tangible
              expressions of this philosophy.
            </p>
            <p className="text-white/75 leading-relaxed pl-2">
              We built Kandi Konnect to honor these connections. Every scan, every transfer
              is a celebration of the community we've built together.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="mb-16 h-px" style={{ background: "rgba(255,255,255,0.06)" }} />

        {/* ── MEET THE TEAM ───────────────────────────────────────────────── */}
        <section className="mb-16">
          <div className="flex items-start gap-5 mb-3">
            <IconOrb gradient="linear-gradient(135deg,#b44fff,#ff3cac)" glow="#b44fff">
              <Users style={{ width: "1.5rem", height: "1.5rem", color: "#fff" }} />
            </IconOrb>
            <div className="pt-1">
              <SectionLabel color="#b44fff">The People</SectionLabel>
              <SectionHeading color="#b44fff" glow="#ff3cac">
                Meet the Team
              </SectionHeading>
            </div>
          </div>
          <p className="text-white/40 text-sm mb-8 ml-[4.75rem]">
            The ravers bringing this vision to life
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {[
              {
                src:   "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=400&h=400&fit=crop",
                name:  "Alex Rivera",
                role:  "Co-Founder & Creative Director",
                bio:   "A festival veteran with 10+ years in the scene, bringing vision and passion for preserving rave culture.",
                color: "#ff3cac",
                alt:   "AR",
              },
              {
                src:   "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop",
                name:  "Jordan Chen",
                role:  "Co-Founder & Lead Developer",
                bio:   "A tech wizard and kandi enthusiast building the platform for seamless kandi tracking.",
                color: "#00f5ff",
                alt:   "JC",
              },
            ].map(({ src, name, role, bio, color, alt }) => (
              <div
                key={name}
                className="rounded-2xl p-6 flex gap-5 transition-all duration-300 hover:-translate-y-1 cursor-default"
                style={{
                  background:     `linear-gradient(135deg, ${color}0e, rgba(255,255,255,0.02))`,
                  border:         `1px solid ${color}28`,
                  backdropFilter: "blur(20px)",
                }}
              >
                {/* Avatar */}
                <div
                  className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
                  style={{ border: `2px solid ${color}60`, boxShadow: `0 0 20px ${color}40` }}
                >
                  <img
                    src={src}
                    alt={name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.parentNode.style.background = `linear-gradient(135deg, ${color}, ${color}88)`;
                      e.target.parentNode.innerHTML = `<span style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:1.1rem">${alt}</span>`;
                    }}
                  />
                </div>

                {/* Text */}
                <div>
                  <h3 className="text-white font-bold mb-0.5">{name}</h3>
                  <p className="text-sm font-semibold mb-2" style={{ color }}>{role}</p>
                  <p className="text-white/55 text-sm leading-relaxed">{bio}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── FOOTER CTA ──────────────────────────────────────────────────── */}
        <section>
          <div
            className="rounded-2xl p-8 text-center relative overflow-hidden"
            style={{
              background:     "linear-gradient(135deg, rgba(180,79,255,0.12), rgba(0,245,255,0.06))",
              border:         "1px solid rgba(180,79,255,0.25)",
              backdropFilter: "blur(20px)",
            }}
          >
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(180,79,255,0.15) 0%, transparent 70%)" }}
            />
            <p className="text-white/50 text-xs uppercase tracking-widest mb-4 relative">Ready to join?</p>
            <h3 className="text-2xl font-black text-white mb-6 relative">
              Start tracking your kandi today
            </h3>
            <button
              onClick={() => navigate("/login")}
              className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full font-bold text-white overflow-hidden cursor-pointer transition-transform hover:scale-105 active:scale-95"
              style={{
                background: "linear-gradient(135deg, #ff3cac, #b44fff, #00f5ff)",
                boxShadow:  "0 0 30px rgba(180,79,255,0.5)",
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <Scan style={{ width: "1.25rem", height: "1.25rem" }} />
                Scan Your First Bracelet
                <ArrowRight
                  style={{ width: "1rem", height: "1rem" }}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 via-purple-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </button>
          </div>
        </section>

      </div>
    </div>
  );
}

export default AboutPage;
